import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import styled from "styled-components";
import { focusRing } from "../../../design-system/focusRing";
import { TOOLTIP_SURFACE } from "../../StyledComponents/tooltipSurface";

// Our own select menu, so a dropdown looks like the rest of the design instead of the
// operating system's native list. Options are read from the <option> children the
// callers already pass, and onChange receives the same {target: {name, value}} shape a
// native select produces — so no call site changes.

// Options are read from the rendered select, not from the React children. A caller may pass a
// component that returns them — <MonthOptions /> is a real example — and a component cannot be
// inspected without rendering it. Reading the DOM also covers optgroups, maps and conditionals
// without a special case for each.
const optionsFromSelect = (select) =>
    Array.from(select ? select.options : []).map((option) => ({
        value: option.value,
        label: option.textContent,
        disabled: option.disabled,
    }));

const Wrapper = styled.div`
    position: relative;
    width: 100%;
    min-width: 0;
`;

// The real control. Consumers validate against it, read its name and value, and attach
// refs to it, so it stays in the DOM and our menu only drives it — building a fake event
// meant guessing every property a consumer might touch, and .validity was the one that
// crashed.
// The native control, shown as-is when the options cannot be read.
// Styled with our own tokens — no Bootstrap class dependency.
const FallbackSelect = styled.select`
    display: block;
    width: 100%;
    box-sizing: border-box;
    min-height: var(--pw-control-height);
    padding: 6px 32px 6px 11px;
    border: 1px solid ${(props) => (props.$invalid ? "var(--pw-danger)" : "var(--border-strong)")};
    border-radius: var(--pw-radius-lg);
    background-color: var(--surface-card);
    color: var(--text-heading);
    font-family: var(--font-body);
    font-size: var(--pw-text-control);
    font-weight: var(--pw-weight-light);
    line-height: var(--pw-leading-normal);
    cursor: pointer;
    appearance: auto;
    transition: border-color var(--pw-transition-fast) var(--pw-ease);

    &:focus {
        border-color: var(--pw-brand-cyan);
        box-shadow: none;
        outline: none;
    }

    &:disabled {
        background-color: var(--pw-neutral-100);
        color: var(--text-muted);
        cursor: default;
    }
`;

const NativeSelect = styled.select`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    border: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
`;

const Trigger = styled.button`
    ${focusRing}
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--gap-m);
    width: 100%;
    min-height: var(--pw-control-height);
    padding: 6px 11px;
    border: 1px solid ${(props) => (props.$invalid ? "var(--pw-danger)" : "var(--border-strong)")};
    border-radius: var(--pw-radius-lg);
    background: ${(props) => (props.disabled ? "var(--pw-neutral-100)" : "var(--surface-card)")};
    color: ${(props) => (props.disabled ? "var(--text-muted)" : "var(--text-heading)")};
    font-family: var(--font-body);
    font-size: var(--pw-text-control);
    font-weight: var(--pw-weight-light);
    line-height: var(--pw-leading-normal);
    text-align: left;
    cursor: ${(props) => (props.disabled ? "default" : "pointer")};
    transition: border-color var(--pw-transition-fast) var(--pw-ease);

    &[aria-expanded="true"] {
        border-color: var(--pw-brand-cyan);
    }
`;

const TriggerLabel = styled.span`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Caret = styled.i`
    flex: none;
    font-size: 11px;
    color: var(--text-muted);
`;

const Menu = styled.ul`
    position: fixed;
    z-index: var(--pw-z-dropdown);
    top: ${(props) => props.$rect.up ? "auto" : props.$rect.top + "px"};
    bottom: ${(props) => props.$rect.up ? props.$rect.bottom + "px" : "auto"};
    left: ${(props) => props.$rect.left}px;
    width: ${(props) => props.$rect.width}px;
    max-height: 260px;
    margin: 0;
    padding: 4px;
    overflow-y: auto;
    list-style: none;
    border: 1px solid var(--border-hairline);
    border-radius: var(--pw-radius-xl);
    background: var(--surface-card);
    box-shadow: var(--pw-shadow-lg);
`;

const Item = styled.li`
    display: flex;
    align-items: center;
    gap: var(--gap-m);
    padding: 7px 10px;
    border-radius: var(--pw-radius-inner);
    color: ${(props) => (props.$disabled ? "var(--text-subtle)" : "var(--text-body)")};
    font-size: var(--pw-text-control);
    font-weight: ${(props) => (props.$selected ? "var(--pw-weight-semibold)" : "var(--pw-weight-light)")};
    cursor: ${(props) => (props.$disabled ? "default" : "pointer")};
    background: ${(props) => (props.$active ? "var(--pw-neutral-75)" : "transparent")};

    ${(props) => props.$selected && "color: var(--pw-brand-cyan);"}
`;

const Check = styled.i`
    flex: none;
    width: 13px;
    font-size: 11px;
    color: var(--pw-brand-cyan);
    visibility: ${(props) => (props.$selected ? "visible" : "hidden")};
`;

const ItemLabel = styled.span`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const PathwaySelectMenu = ({
    name,
    value,
    children,
    onChange,
    onBlur,
    disabled = false,
    invalid = false,
    dataCy,
    ariaLabel,
    className,
}) => {
    const [options, setOptions] = useState([]);
    const canRenderMenu = options.length > 0;
    const selectedIndex = options.findIndex((option) => option.value === String(value ?? ""));
    // The first option is sometimes a disabled placeholder (value="", label="Bitte wählen"). It must
    // not appear in the dropdown list, but the trigger still uses it as a fallback label when nothing
    // is selected — so the full options array is kept and only the render skips it.
    const firstSelectableIndex = options.findIndex((o) => !(o.disabled && o.value === ""));

    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(selectedIndex < 0 ? Math.max(firstSelectableIndex, 0) : selectedIndex);
    const [truncatedIndex, setTruncatedIndex] = useState(null);
    const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
    const wrapperRef = useRef(null);
    const triggerRef = useRef(null);
    const nativeRef = useRef(null);
    const labelRefs = useRef({});
    // uid only used to generate stable element ids — suppress the useId warning
    // eslint-disable-next-line no-unused-vars
    const uid = useId().replace(/[^a-zA-Z0-9]/g, "");

    const [rect, setRect] = useState({ top: 0, left: 0, width: 0, up: false });

    // The select is always rendered, so its options can be read after mount and whenever the
    // children change. Until then the menu has nothing to show and the select stands in.
    useEffect(() => {
        setOptions(optionsFromSelect(nativeRef.current));
    }, [children]);

    const close = useCallback(() => {
        setOpen(false);
        // The hook's onBlur reads and writes e.target.value, so it needs a real event
        // shape — calling it bare threw "Cannot read properties of undefined".
        if (onBlur && nativeRef.current) onBlur({ target: nativeRef.current });
    }, [onBlur]);

    const placeMenu = useCallback(() => {
        if (!triggerRef.current) return true;
        const box = triggerRef.current.getBoundingClientRect();
        // Flip the menu upward when there is not enough room below (< 200 px) and more
        // space is available above. This prevents the dropdown from running off-screen in
        // large modals and on small viewports.
        const spaceBelow = window.innerHeight - box.bottom;
        const openUpward = spaceBelow < 200 && box.top > spaceBelow;
        if (openUpward) {
            setRect({ bottom: window.innerHeight - box.top + 4, left: box.left, width: box.width, up: true });
        } else {
            setRect({ top: box.bottom + 4, left: box.left, width: box.width, up: false });
        }
        // A fixed menu does not scroll with its trigger, so once the trigger leaves the
        // viewport the menu hangs in empty space with only its border showing. Report that so
        // the caller can close it.
        return box.bottom > 0 && box.top < window.innerHeight;
    }, []);

    // A click anywhere else closes the menu; without this it survives navigation to
    // another field and floats over the form.
    // Measuring on click alone catches the trigger mid-animation — inside a modal that is
    // still fading in, its rect is not where it ends up, and the menu landed over the
    // trigger instead of below it. Re-measure once the menu is actually open.
    useEffect(() => {
        if (open) placeMenu();
    }, [open, placeMenu]);

    useEffect(() => {
        if (!open) return undefined;

        const onDocumentClick = (event) => {
            const insideTrigger = wrapperRef.current && wrapperRef.current.contains(event.target);
            const insideMenu = event.target.closest && event.target.closest('[role="listbox"]');
            if (!insideTrigger && !insideMenu) close();
        };

        const reposition = () => {
            if (!placeMenu()) close();
        };

        document.addEventListener("mousedown", onDocumentClick);
        window.addEventListener("resize", reposition);
        window.addEventListener("scroll", reposition, true);
        return () => {
            document.removeEventListener("mousedown", onDocumentClick);
            window.removeEventListener("resize", reposition);
            window.removeEventListener("scroll", reposition, true);
        };
    }, [open, close, placeMenu]);

    // Setting .value and dispatching a real change event lets the caller's own onChange
    // fire with a genuine target — including validity, which a hand-made event lacks.
    const select = (option) => {
        if (option.disabled || !nativeRef.current) return;

        // The select is React-controlled, so assigning .value directly is reverted before
        // the event is read. Going through the prototype's setter updates React's own value
        // tracker, which is what makes the dispatched event carry the new value.
        const setValue = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, "value").set;
        setValue.call(nativeRef.current, option.value);
        nativeRef.current.dispatchEvent(new Event("change", { bubbles: true }));
        setOpen(false);
        if (triggerRef.current) triggerRef.current.focus();
    };

    const onKeyDown = (event) => {
        if (disabled) return;

        if (!open && ["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) {
            event.preventDefault();
            setActiveIndex(selectedIndex < 0 ? Math.max(firstSelectableIndex, 0) : selectedIndex);
            placeMenu();
            setOpen(true);
            return;
        }
        if (!open) return;

        if (event.key === "Escape") {
            event.preventDefault();
            close();
        } else if (event.key === "ArrowDown") {
            event.preventDefault();
            setActiveIndex((index) => {
                const next = Math.min(index + 1, options.length - 1);
                return options[next]?.disabled && options[next]?.value === "" ? index : next;
            });
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex((index) => {
                const next = Math.max(index - 1, 0);
                return options[next]?.disabled && options[next]?.value === "" ? index : next;
            });
        } else if (event.key === "Home") {
            event.preventDefault();
            setActiveIndex(Math.max(firstSelectableIndex, 0));
        } else if (event.key === "End") {
            event.preventDefault();
            setActiveIndex(options.length - 1);
        } else if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            if (options[activeIndex]) select(options[activeIndex]);
        } else if (event.key === "Tab") {
            close();
        }
    };

    // A native select shows its first option when the current value matches none — an unset field then reads
    // "Bitte Shop auswählen" rather than as an empty box the user has to open to understand.
    const selectedLabel =
        selectedIndex >= 0 ? options[selectedIndex].label : options.length > 0 ? options[0].label : "";
    const listId = dataCy ? `${dataCy}-listbox` : undefined;

    if (!canRenderMenu) {
        return (
            <FallbackSelect
                ref={nativeRef}
                name={name}
                value={value === null || value === undefined ? "" : String(value)}
                onChange={onChange}
                onBlur={onBlur}
                disabled={disabled}
                data-cy={dataCy}
                aria-label={ariaLabel}
                className={className}
                $invalid={invalid}
            >
                {children}
            </FallbackSelect>
        );
    }

    return (
        <Wrapper ref={wrapperRef} className={className}>
            <NativeSelect
                ref={nativeRef}
                name={name}
                value={value === null || value === undefined ? "" : String(value)}
                onChange={onChange}
                disabled={disabled}
                tabIndex={-1}
                aria-hidden="true"
            >
                {children}
            </NativeSelect>

            <Trigger
                ref={triggerRef}
                type="button"
                role="combobox"
                aria-expanded={open}
                aria-haspopup="listbox"
                aria-controls={listId}
                aria-label={ariaLabel}
                aria-invalid={invalid || undefined}
                $invalid={invalid}
                disabled={disabled}
                data-cy={dataCy}
                onClick={() => {
                    if (disabled) return;
                    placeMenu();
                    setOpen((wasOpen) => !wasOpen);
                }}
                onKeyDown={onKeyDown}
            >
                <TriggerLabel>{selectedLabel}</TriggerLabel>
                <Caret className="fa fa-chevron-down" aria-hidden="true" />
            </Trigger>

            {open &&
                createPortal(
                    <Menu id={listId} role="listbox" aria-label={ariaLabel} data-cy={listId} $rect={rect}>
                    {options.map((option, index) => {
                        if (option.disabled && option.value === "") return null;
                        return (
                        <Item
                            key={option.value + "-" + index}
                            role="option"
                            aria-selected={index === selectedIndex}
                            aria-disabled={option.disabled || undefined}
                            $selected={index === selectedIndex}
                            $active={index === activeIndex}
                            $disabled={option.disabled}
                            onMouseEnter={() => {
                                setActiveIndex(index);
                                const el = labelRefs.current[index];
                                if (el && el.scrollWidth > el.offsetWidth) {
                                    const r = el.getBoundingClientRect();
                                    setTooltipPos({ top: r.top + r.height / 2, left: r.right + 8 });
                                    setTruncatedIndex(index);
                                } else {
                                    setTruncatedIndex(null);
                                }
                            }}
                            onMouseLeave={() => setTruncatedIndex(null)}
                            onClick={() => select(option)}
                        >
                            <Check className="fa fa-check" $selected={index === selectedIndex} aria-hidden="true" />
                            <ItemLabel ref={(el) => { labelRefs.current[index] = el; }}>
                                {option.label}
                            </ItemLabel>
                        </Item>
                        );
                    })}
                    {truncatedIndex !== null && options[truncatedIndex] && createPortal(
                        <div style={{
                            position: "fixed",
                            top: tooltipPos.top,
                            left: tooltipPos.left,
                            transform: "translateY(-50%)",
                            zIndex: 99999,
                            pointerEvents: "none",
                            maxWidth: 320,
                            padding: "4px 10px",
                            wordBreak: "break-word",
                            ...TOOLTIP_SURFACE,
                        }}>
                            {options[truncatedIndex].label}
                        </div>,
                        document.body
                    )}
                    </Menu>,
                    document.body
                )}
        </Wrapper>
    );
};

PathwaySelectMenu.propTypes = {
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    children: PropTypes.node,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    disabled: PropTypes.bool,
    invalid: PropTypes.bool,
    dataCy: PropTypes.string,
    ariaLabel: PropTypes.string,
    className: PropTypes.string,
};

export default PathwaySelectMenu;
